/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import {
  HOTSITE_INDEXES,
  LISTING_INDEXES,
  SEARCH_INDEXES,
  SEARCH_WEB_INDEXES,
  WISHLIST_INDEXES,
} from '@helpers/algolia/constants';
import { getProductListOrigin } from '@helpers/analytics/productListOrigin';
import trackEvent from 'event-tracks';
import { ProductEvents } from 'types/analytics/product/product.enum';
import type { TListViewedProps } from 'types/analytics/product/product.types';

export interface ItrackObserverListViewProps {
  visible?: boolean;
  products?: any[];
  customProducts?: any[];
  index?: string;
  listId?: string;
  isFiltered?: boolean;
  listShippingFilter?: boolean;
  indexWishlist?: string;
  listType?: string;
}

export default function trackListView({
  visible = true,
  customProducts = null,
  products = null,
  index = '',
  listId,
  isFiltered,
  listShippingFilter = false,
  indexWishlist,
  listType,
}: ItrackObserverListViewProps): void {
  if (!visible || (!products?.length && !customProducts)) return null;

  const listSort = indexWishlist || index;

  const eventData: TListViewedProps = {
    event: 'ListView',
    event_date: new Date(),
    list_id: listId,
    list_filtered: isFiltered,
    list_shipping_filter: listShippingFilter,
    list_sort: getIndexValue(listSort),
    list_type: listType,
    ...(listId?.includes('Busca') && {
      search_id: sessionStorage.getItem('mm_analytics_searchId'),
    }),
    products:
      customProducts ||
      products.map((element: any) => ({
        product_sku: element?.id_produto || element?.id || element?.product_id || null,
        product_name: element?.nome || element?.name || null,
        product_category_tree: productCategoryTreeFormatter(element),
        product_new_category:
          element?.new_category || element?.product_new_category || 'Sem Categoria',
        product_category: productCategoryFormatter(element),
        product_category_id: productCategoryId(element),
        product_brand: element?.marca || element?.brand || null,
        product_price:
          element?.preco_por || element?.buyBox?.[0]?.price?.total || element?.price || null,
        product_alg_object_id: element?.objectID || null,
        product_alg_query_id: element?.queryID || element?.product_alg_query_id || null,
        product_alg_index: element?.index || index || null,
        product_type: productTypeFormatter(element),
        product_seller: productSellerFormatter(element),
        product_seller_id: productSellerIdFormatter(element),
        product_list_origin: getProductListOrigin(
          element?.id_produto || element?.id || element?.product_id || element?.objectID || null,
          false,
          listId,
          !!element?.sponsoredData
        ),
      })),
  };
  return trackEvent(ProductEvents.ListView, eventData);
}

function stringNormalizer(str: string): string {
  if (!str) {
    return null;
  }
  return str
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s/g, '-')
    .toLowerCase();
}

function getIndexValue(elementIndex: string, indexName?: string): string {
  const index = elementIndex || indexName;
  const listLabel = LISTING_INDEXES.find((element: any) => element.value === index);
  const hotsiteLabel = HOTSITE_INDEXES.find((element: any) => element.value === index);
  const searchLabel = SEARCH_INDEXES.find((element: any) => element.value === index);
  const searchWithNewIndexLabel = SEARCH_WEB_INDEXES.find(
    (element: any) => element.value === index
  );
  const wishlistLabel = WISHLIST_INDEXES.find((element: any) => element.value === index);

  return (
    listLabel?.label ||
    hotsiteLabel?.label ||
    searchLabel?.label ||
    searchWithNewIndexLabel?.label ||
    wishlistLabel?.label ||
    index ||
    null
  );
}

function productCategoryFormatter(element: any) {
  return (
    element?.category_tree?.split('/')?.pop() ||
    stringNormalizer(element?.product_category) ||
    stringNormalizer(element?.familia) ||
    stringNormalizer(element?.urls?.category.split('/')?.pop()) ||
    stringNormalizer(element?.category_name) ||
    stringNormalizer(element?.category) ||
    null
  );
}

function productCategoryTreeFormatter(element: any) {
  return (
    element?.category_tree ||
    element?.product_category_tree ||
    element?.breadcrumb?.slice(-1)?.url ||
    element?.urls?.category ||
    null
  );
}

function productCategoryId(element: any) {
  return (
    element?.id_categoria ||
    element?.category_id ||
    element?.product_category_id ||
    element?.category ||
    null
  );
}

function productSellerFormatter(element: any) {
  return (
    element?.seller_slug ||
    element?.nome_seller ||
    element?.buyBox?.[0]?.seller?.slug ||
    element?.product_seller ||
    null
  );
}

function productSellerIdFormatter(element: any) {
  return element?.buyBox?.[0].seller?.id || element?.product_seller_id || null;
}

function productTypeFormatter(element: any) {
  return element?.type || element?.product_type || Number(productSellerIdFormatter(element)) === 1
    ? '1p'
    : '3p';
}
